import styled from '@emotion/styled'
import { Button, Icon } from '@mobi/component-library/Common/V2'
import { colors, spacing } from '@mobi/component-library/Theme/Common'
import React, { useState } from 'react'
import { Device } from '@mobi/api-types'
import dayjs from '@mobi/utils/date/extendedDayjs'
import { addToast } from '@mobi/component-library/Feedback/Toast'

type Props = {
  device: Device
  canTrust: boolean
  onDelete: (deviceKey: string) => Promise<void>
  onTrustDevice: () => Promise<void>
}

export const MFADeviceItem: React.FC<Props> = ({ device, canTrust, onDelete, onTrustDevice }) => {
  const deviceType = getDeviceType(device)
  const isDeviceTrusted = device.deviceStatus === 'valid'
  const [isDeleting, setIsDeleting] = useState(false)
  const [isTrusting, setIsTrusting] = useState(false)

  const handleTrustClick = async () => {
    if (!canTrust) {
      addToast({
        id: 'max-trusted-devices',
        title: 'Max trusted devices reached',
        subtitle: 'Remove one to add a new device.',
        icon: 'error',
        isDismissable: true,
      })
      return
    }
    try {
      setIsTrusting(true)
      await onTrustDevice()
    } finally {
      setIsTrusting(false)
    }
  }

  return (
    <DeviceItemStyled>
      <Icon
        name={
          deviceType === 'mobile'
            ? 'LinePhone01'
            : deviceType === 'tablet'
              ? 'LineTablet01'
              : 'LineLaptop02'
        }
      />
      <p>
        {device.deviceName}
        <br />
        {device.deviceLastAuthenticatedDate && (
          <small>Last login {dayjs(device.deviceLastAuthenticatedDate).fromNow()}</small>
        )}
      </p>
      {isDeviceTrusted ? (
        <Button
          size='xs'
          color='secondary_destructive'
          isIconOnlyButton={true}
          disabled={isDeleting}
          isLoading={isDeleting}
          onClick={async () => {
            try {
              setIsDeleting(true)
              await onDelete(device.deviceKey)
            } finally {
              setIsDeleting(false)
            }
          }}
        >
          <Icon name='LineTrash04' />
        </Button>
      ) : (
        <Button
          size='xs'
          color='positive'
          disabled={isTrusting}
          isLoading={isTrusting}
          onClick={handleTrustClick}
        >
          Trust
        </Button>
      )}
    </DeviceItemStyled>
  )
}

export const getDeviceType = (device: Device) => {
  const deviceName = device.deviceName.toLowerCase()

  if (/ipad|tablet/.test(deviceName)) {
    return 'tablet'
  }

  if (/android|ios|iphone/.test(deviceName)) {
    return 'mobile'
  }

  if (/linux|mac|windows pc|edge|browser/i.test(deviceName)) {
    return 'web'
  }

  return 'web' // default fallback
}

// ======
// Styles
// ======

const DeviceItemStyled = styled.div({
  display: 'flex',
  gap: spacing.smx2,
  alignItems: 'center',
  marginBottom: '2rem',

  '& > p': {
    flex: 1,
    margin: 0,
    lineHeight: '1.6rem',
  },

  '& small': {
    color: colors.neutral[800],
  },
})
